import { useId, useRef, useState } from 'react';
import {
  Box,
  Button,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AffiliateListRow } from '@/api/generated';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/useToast';
import { visuallyHidden } from '@/components/visuallyHidden';
import { useMenuFocusReturn } from '@/features/members/useMenuFocusReturn';
import { useCanWrite } from '@/auth/authHooks';
import { useTenantLink } from '@/tenant/useTenant';
import { NumericCell } from '@/components/cells';
import { StatusChip } from '@/components/status/StatusChip';
import { resolveTokens } from '@/theme';
import { moneyIn } from '@/components/moneyIn';
import { useLocale } from '@/i18n/useLocale';
import { payoutReadiness } from './affiliateLabels';
import { isPlanRequired, planRefOf } from './affiliatePlan';
import { useActionError, type ActionError } from '@/components/useActionError';

export interface AffiliateListProps {
  affiliates: AffiliateListRow[];
  query: string;
  selectedId: number | undefined;
  onQuery: (query: string) => void;
  onInvite: () => void;
  onSettings: () => void;
  onApprove: (id: number) => Promise<void>;
  onDecline: (id: number) => Promise<void>;
  /** Refetch after a stale row: approving `id` answered `409 COMMISSION_PLAN_REQUIRED`. */
  onReload: (id: number) => void;
  showToolbarActions: boolean;
}

export function AffiliateList({
  affiliates,
  query,
  selectedId,
  onQuery,
  onInvite,
  onSettings,
  onApprove,
  onDecline,
  onReload,
  showToolbarActions,
}: AffiliateListProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const navigate = useNavigate();
  const showToast = useToast();
  const describe = useActionError();
  const link = useTenantLink();
  // A record's link keeps the list's query, so Close returns to the same list (TMM-B6).
  const { search } = useLocation();
  const recordOf = (id: number) => ({ pathname: link(`affiliates/${id}`), search });
  const headingId = useId();
  const canWrite = useCanWrite();
  const [declining, setDeclining] = useState<AffiliateListRow | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<Pick<ActionError, 'message' | 'requestId'> | null>(
    null,
  );
  // A declined applicant's row no longer offers Decline, so focus returns to the heading (DLG-004).
  const heading = useRef<HTMLHeadingElement>(null);
  const focusReturn = useMenuFocusReturn(heading);

  const q = query.trim().toLowerCase();
  const visible = affiliates.filter((row) => {
    if (!q) return true;
    return (
      row.displayName.toLowerCase().includes(q) || row.email.toLowerCase().includes(q)
    );
  });

  async function handleApprove(row: AffiliateListRow) {
    setActing(true);
    setActionError(null);
    try {
      await onApprove(row.id);
      showToast(t('affiliates.toast.approved', { name: row.displayName }));
    } catch (err) {
      const planRequired = isPlanRequired(err);
      const action = describe(err);
      setActionError({
        message: planRequired ? t('affiliates.error.planRequired') : action.message,
        requestId: action.requestId,
      });
      if (planRequired) onReload(row.id);
    } finally {
      setActing(false);
    }
  }

  async function handleDecline() {
    if (declining == null) return;
    await onDecline(declining.id);
    showToast(
      t('affiliates.toast.declined', {
        name: declining.displayName,
      }),
    );
    focusReturn.rowGone();
  }

  return (
    <Box>
      <Typography
        variant="h2"
        component="h2"
        id={headingId}
        tabIndex={-1}
        ref={heading}
        sx={visuallyHidden}
      >
        {t('affiliates.listHeading')}
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          label={t('affiliates.search')}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
        />
        {showToolbarActions ? (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Button onClick={onInvite}>{t('affiliates.invite')}</Button>
            <Button onClick={onSettings}>{t('affiliates.settings')}</Button>
          </Stack>
        ) : null}
        <ActionErrorAlert error={actionError} />
      </Stack>
      {visible.length === 0 && affiliates.length === 0 ? (
        <EmptyState kind="none" title={t('affiliates.empty')} />
      ) : null}
      {visible.length === 0 && affiliates.length > 0 ? (
        <EmptyState kind="noMatch" title={t('affiliates.noMatch')} onClear={() => onQuery('')} />
      ) : null}
      {visible.length === 0 ? null : (
        <Table size="small" aria-labelledby={headingId}>
          <TableHead>
            <TableRow>
              <TableCell>{t('affiliates.col.name')}</TableCell>
              <TableCell>{t('affiliates.col.status')}</TableCell>
              <TableCell>{t('affiliates.col.plan')}</TableCell>
              <TableCell align="right">{t('affiliates.col.referred')}</TableCell>
              <TableCell align="right">
                {t('affiliates.col.commission')}
              </TableCell>
              <TableCell>{t('affiliates.col.payout')}</TableCell>
              <TableCell>{t('affiliates.col.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((row) => {
              const selected = row.id === selectedId;
              const readiness = payoutReadiness(row.agreement);
              const plan = planRefOf(row.agreement);
              return (
                <TableRow key={row.id} hover selected={selected}>
                  <TableCell component="th" scope="row">
                    <Link
                      component={RouterLink}
                      to={recordOf(row.id)}
                      aria-current={selected ? 'true' : undefined}
                    >
                      {row.displayName}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                      <StatusChip domain="affiliate" status={row.status} />
                      {row.payoutsHeldForReview ? (
                        <StatusChip domain="affiliateHold" status="REVIEW" />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.status === 'APPLIED' || !plan
                      ? t('affiliates.unavailable')
                      : plan.name || t('commissionPlans.unnamed')}
                  </TableCell>
                  <NumericCell value={row.referredActive} />
                  <TableCell align="right">
                    {row.performance.length ? (
                      <Stack>
                        {row.performance.map((performance) => (
                          <Box key={performance.currency} sx={{ whiteSpace: 'nowrap' }}>
                            {moneyIn(
                              performance.commission30dMinor,
                              performance.currency,
                              locale,
                              t('affiliates.unavailable'),
                            )}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      t('affiliates.unavailable')
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusChip domain="affiliatePayout" status={readiness} />
                  </TableCell>
                  <TableCell>
                    {row.status === 'APPLIED' && canWrite ? (
                      <Stack direction="row" spacing={0.75}>
                        {plan ? (
                          <Button
                            size="small"
                            disabled={acting}
                            onClick={() => void handleApprove(row)}
                          >
                            {t('affiliates.approve')}
                          </Button>
                        ) : (
                          // Approving needs a deal that names a plan: lead there, not a dead button.
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(recordOf(row.id), { state: { focusDeal: true } })
                            }
                          >
                            {t('affiliates.setUpDeal')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          disabled={acting}
                          onClick={(event) => {
                            focusReturn.remember(event.currentTarget);
                            setDeclining(row);
                          }}
                        >
                          {t('affiliates.decline')}
                        </Button>
                      </Stack>
                    ) : (
                      <Button size="small" onClick={() => navigate(recordOf(row.id))}>
                        {t('affiliates.open')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Typography variant="body2" sx={{ color: tokens.text2, mt: 1.5 }}>
        {t('affiliates.count', { count: visible.length })}
      </Typography>
      <ConfirmDialog
        open={declining !== null}
        title={t('affiliates.declineTitle', {
          name: declining ? declining.displayName : '',
        })}
        consequence={t('affiliates.declineBody')}
        confirmLabel={t('affiliates.declineConfirm')}
        destructive
        onConfirm={handleDecline}
        onCancel={() => {
          setDeclining(null);
          focusReturn.dialogClosed();
        }}
        errorMessage={(err) => describe(err).message}
      />
    </Box>
  );
}
